import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
// UserLevelPermissionPage.tsx — Co Marca Admin — estilo CategoryPage
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Edit, Trash2, Plus, History, ShieldCheck, ChevronRight, Home, Check, X as XIcon } from 'lucide-react';
import { fetchModelPermissionsByFilter } from '../../../services/modelPermissionsService';
import CreatePermissionModal from '../../../components/ModelPermission/CreatePermissionModal';
import EditPermissionModal from '../../../components/ModelPermission/EditPermissionModal';
import DeletePermissionModal from '../../../components/ModelPermission/DeletePermissionModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

interface ModelPermission {
    id: number; user_level: number; model_name: string;
    can_create: boolean; can_read: boolean; can_update: boolean; can_delete: boolean;
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `

.ulp-page { font-family:'DM Sans',sans-serif; }
@keyframes ulpFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.ulp-fadeup   { animation:ulpFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.ulp-fadeup-1 { animation:ulpFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
@keyframes spin { to{transform:rotate(360deg);} }
.ulp-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.ulp-btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32); }
.ulp-btn-secondary {
    display:inline-flex;align-items:center;gap:7px;
    background:var(--ad-surface, #fff);border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151);border-radius:11px;
    padding:9px 16px;font-size:.83rem;font-weight:700;
    cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .2s;
}
.ulp-btn-secondary:hover { background:var(--ad-soft, #f8fafc); border-color:color-mix(in srgb, ${colors.secondary} 25.1%, transparent); color:var(--ad-link, #2854c5); }
.ulp-table-row { transition:background .15s; }
.ulp-table-row:hover { background:var(--ad-soft, #fafcff); }
.ulp-action-btn { width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s; }
.ulp-perm-badge {
    display:inline-flex;align-items:center;gap:4px;
    border-radius:7px;padding:3px 9px;font-size:.68rem;font-weight:700;
    white-space:nowrap;
}
.custom-scroll::-webkit-scrollbar { height:6px;width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color:rgba(97, 37, 123,0.3);border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color:rgba(97, 37, 123,0.5); }
`;

const PERM_CONFIG = {
    can_create: { label:'Crear',   color:'var(--ad-link, #2854c5)',      bg:`color-mix(in srgb, ${colors.secondary} 7.06%, transparent)`,      border:`color-mix(in srgb, ${colors.secondary} 14.51%, transparent)` },
    can_read:   { label:'Ver',     color:'var(--ad-link, #2854c5)',  bg:`color-mix(in srgb, ${colors.secondaryLight} 7.84%, transparent)`, border:`color-mix(in srgb, ${colors.secondaryLight} 18.82%, transparent)` },
    can_update: { label:'Editar',  color: colors.accentDark,      bg:`color-mix(in srgb, ${colors.accentDark} 7.06%, transparent)`,     border:`color-mix(in srgb, ${colors.accentDark} 14.51%, transparent)` },
    can_delete: { label:'Eliminar',color:'var(--ad-link, #2854c5)',         bg:`color-mix(in srgb, ${colors.primary} 6.27%, transparent)`,        border:`color-mix(in srgb, ${colors.primary} 13.33%, transparent)` },
} as const;

const UserLevelPermissionPage: React.FC = () => {
    const [permissions, setPermissions]         = useState<ModelPermission[]>([]);
    const [loading, setLoading]                 = useState(true);
    const [isModalOpen, setIsModalOpen]         = useState(false);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const userLevelId = parseInt(id||'0');
    const [editingPerm, setEditingPerm]         = useState<ModelPermission|null>(null);
    const [deletingPerm, setDeletingPerm]       = useState<ModelPermission|null>(null);
    const [currentPage, setCurrentPage]         = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const currentUserLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('modelpermission', currentUserLevelId);

    const loadPermissions = useCallback(async () => {
        if (!perm?.can_read) { setPermissions([]); setLoading(false); return; }
        try {
            setLoading(true);
            const data = await fetchModelPermissionsByFilter({ user_level: userLevelId });
            setPermissions(data);
        } catch { setPermissions([]); }
        finally { setLoading(false); }
    }, [userLevelId, perm?.can_read]);

    useEffect(() => { loadPermissions(); }, [loadPermissions]);

    const sortedPermissions  = useMemo(() => [...permissions].sort((a,b)=>a.id-b.id), [permissions]);
    const totalPages         = Math.ceil(sortedPermissions.length / itemsPerPage);
    const displayedPermissions = sortedPermissions.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    if (perm === null) return (
        <>
            <style>{css}</style>
            <div className="ulp-page" style={{ background:'var(--ad-surface, #fff)', minHeight:'40vh', display:'flex', alignItems:'center', justifyContent:'center' }} data-admin-page="true">
                <p style={{ color:'var(--ad-muted, #64748b)', fontWeight:'600' }}>Cargando permisos...</p>
            </div>
        </>
    );

    return <><LumaResourceWorkspace title="Permisos por modelo" description="Consulta y gestiona la información de tu coworking." group="Usuarios" rows={permissions} columns={[{key:'model_name',label:'Modelo'},...(['can_create','can_read','can_update','can_delete'] as const).map((key,i)=>({key,label:['Crear','Leer','Editar','Eliminar'][i],render:(r:any)=><span className={`luma-badge ${r[key]?'success':'warning'}`}>{r[key]?'Permitido':'Sin permiso'}</span>}))]} idKey="id" nameKey="model_name" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsModalOpen(true)}:undefined} onEdit={perm?.can_update?setEditingPerm:undefined} onDelete={perm?.can_delete?setDeletingPerm:undefined}><p className="luma-feedback">Permisos del nivel de usuario #{userLevelId}</p></LumaResourceWorkspace><CreatePermissionModal isOpen={isModalOpen} onClose={()=>setIsModalOpen(false)} userLevelId={userLevelId} onPermissionCreated={loadPermissions}/><EditPermissionModal isOpen={!!editingPerm} onClose={()=>setEditingPerm(null)} userLevelId={userLevelId} initialData={editingPerm||{} as ModelPermission} onPermissionUpdated={loadPermissions}/><DeletePermissionModal isOpen={!!deletingPerm} onClose={()=>setDeletingPerm(null)} permission={deletingPerm||undefined} onPermissionDeleted={loadPermissions}/><ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/></>;
};

export default UserLevelPermissionPage;
